import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { api, money } from "@/lib/api";
import { track } from "@/lib/pixel";
import { useAuth } from "@/context/AuthContext";

export default function CheckoutSuccess() {
  const { orderId } = useParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("VERIFYING"); // VERIFYING | COMPLETED | PENDING | FAILED | NOT_FOUND
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    let stopped = false;
    let timer;
    const tick = async () => {
      if (stopped) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/phonepe/status/${orderId}`);
        if (stopped) return;
        setOrder(data.order || null);
        const s = (data.state || "PENDING").toUpperCase();
        if (s === "COMPLETED") {
          setState("COMPLETED");
          const ord = data.order || {};
          // Fire Meta Pixel Purchase — guard against duplicate fires on component re-mount
          if (!window.__pxPurchased || window.__pxPurchased !== orderId) {
            window.__pxPurchased = orderId;
            track("Purchase", {
              value: Number(ord.final_price || 0),
              currency: "INR",
              content_ids: ord.course_id ? [ord.course_id] : undefined,
              content_name: ord.course_title,
              content_type: "product",
              num_items: 1,
              transaction_id: orderId,
            });
          }
          await refresh();
          return;
        }
        if (s === "FAILED") { setState("FAILED"); return; }
        setState("PENDING");
        // Poll for up to ~90s (18 attempts x 5s) then stop and let user refresh
        if (attempts.current < 18) timer = setTimeout(tick, 5000);
      } catch (e) {
        if (stopped) return;
        if (e?.response?.status === 404) { setState("NOT_FOUND"); return; }
        if (attempts.current < 18) timer = setTimeout(tick, 5000);
      }
    };
    tick();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
  }, [orderId, refresh]);

  return (
    <PublicLayout>
      <section className="container-x py-24 max-w-xl">
        <div className="glass rounded-2xl p-10 text-center" data-testid="checkout-success">
          {state === "COMPLETED" && (
            <>
              <CheckCircle2 className="w-14 h-14 text-gold mx-auto" />
              <h1 className="font-display text-4xl font-black tracking-tighter mt-6">Welcome to your AI journey.</h1>
              <p className="mt-4 text-ink-600">Your enrolment is confirmed. A copy of your order has been logged to your account.</p>
              {order && (
                <div className="mt-6 text-sm text-ink-700">
                  <div>{order.course_title}</div>
                  <div className="text-ink-500 mt-1">Amount paid: {money(order.final_price)}</div>
                </div>
              )}
              <div className="mt-2 text-xs text-ink-500">Order ID: {orderId}</div>
              <Link to="/dashboard" className="btn-neon mt-8 inline-flex" data-testid="success-goto-dashboard">Go to your dashboard</Link>
            </>
          )}
          {(state === "VERIFYING" || state === "PENDING") && (
            <>
              <Loader2 className="w-14 h-14 text-gold mx-auto animate-spin" />
              <h1 className="font-display text-3xl font-black tracking-tighter mt-6">Confirming your payment…</h1>
              <p className="mt-4 text-ink-600">This usually takes a few seconds. Please don't close this page.</p>
              <div className="mt-6 text-xs text-ink-500">Order ID: {orderId}</div>
            </>
          )}
          {state === "FAILED" && (
            <>
              <XCircle className="w-14 h-14 text-red-500 mx-auto" />
              <h1 className="font-display text-3xl font-black tracking-tighter mt-6">Payment not completed</h1>
              <p className="mt-4 text-ink-600">Your card/UPI wasn't charged. You can try again anytime — no course access has been granted.</p>
              <div className="mt-6 text-xs text-ink-500">Order ID: {orderId}</div>
              <Link to="/courses" className="btn-neon mt-8 inline-flex" data-testid="success-back-courses">Back to courses</Link>
            </>
          )}
          {state === "NOT_FOUND" && (
            <>
              <XCircle className="w-14 h-14 text-red-500 mx-auto" />
              <h1 className="font-display text-3xl font-black tracking-tighter mt-6">Order not found</h1>
              <p className="mt-4 text-ink-600">We couldn't locate this order. If you were charged, please contact support with your PhonePe transaction ID.</p>
              <Link to="/courses" className="btn-neon mt-8 inline-flex">Back to courses</Link>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
